import { useState, useEffect, useCallback } from 'react';
import {
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useToast } from '../../hooks/toast';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import GuideStatusCreateEdit from './CreateEdit';
import { ColorContainer } from './styles';
import { IGuideStatus } from '../../shared/interfaces';
import { getGuidesStatus } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const GuidesStatus: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [guideStatus, setGuideStatus] = useState<IGuideStatus | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [guidesStatus, setGuidesStatus] = useState<IGuideStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const list = useCallback(async () => {
    setLoading(true);
    setGuidesStatus([]);
    try {
      const response = await getGuidesStatus(limit, (page - 1) * limit, search);
      setPages(response.count);
      setGuidesStatus(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar planos de contas',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalGuideStatusCreateEdit = () => {
    if (modalCreate)
      return (
        <GuideStatusCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          guideStatus={guideStatus}
          setGuidesStatus={setGuidesStatus}
        />
      );
  };

  useEffect(() => {
    list();
  }, [page, search, limit, list]);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<UnorderedListOutlined />}
        title="Status de guia"
        description="Padronize os status de guias com identificação visual consistente para facilitar leitura e operação financeira."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.guidesStatusCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setGuideStatus(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Id',
              width: 100,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Cor',
              dataIndex: 'color',
              render: (value: string) => <ColorContainer color={value} />,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IGuideStatus) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.guidesStatusUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setGuideStatus(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={guidesStatus}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalGuideStatusCreateEdit()}
    </PageLayout>
  );
};

export default GuidesStatus;
